// How the reader moves through the magazine: the pinned horizontal strip on desktop, the
// stacked vertical page on mobile, the header menu, and jumps to `#article-{id}`.
//
// Both layouts fill in `mag.goToArticle(id, animate)`, so the navigation at the bottom
// doesn't have to care which layout it is driving, nor how long that layout's jump takes.

mag.goToArticle = null;
mag.scrollTween = null;

mag.initMenu = (session) => {
    const { signal } = session;

    // Delegated, because SiteHeader re-renders the menu over the life of the circuit
    document.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target) {
            return;
        }
        if (target.closest('.site-header__menu_icon')) {
            document.body.classList.toggle('open-menu');
        } else if (target.closest('.close-menu-icon, .site-header__menu .menu a')) {
            document.body.classList.remove('open-menu');
        }
    }, { signal });

    // accessory spots: show the result the clicked spot points at
    document.addEventListener('click', (e) => {
        const spot = e.target instanceof Element ? e.target.closest('.acc_spot') : null;
        if (!spot) {
            return;
        }
        document.querySelectorAll('.perfect_acc_result').forEach(el => el.classList.remove('pr-show-acc', 'pr-start-acc'));
        document.getElementById(spot.getAttribute('data-acc-spot'))?.classList.add('pr-show-acc');
    }, { signal });
};

// How long a jump takes: longer the further it goes, so a short hop doesn't crawl and a
// jump across the whole magazine doesn't snap. `distance` in pixels of scroll.
mag.jumpDuration = (distance, min, max, perPixel) => Math.min(Math.max(min + Math.abs(distance) * perPixel, min), max);

mag.initDesktopScroller = (layout, deepLinkId) => {
    const scrollContainer = document.querySelector('.wrapper');

    // the strip's length as laid out (summing each article's rounded offsetWidth drifted by
    // a pixel per article)
    let maxWidth = 0;
    const getMaxWidth = () => {
        maxWidth = scrollContainer.scrollWidth;
    };
    getMaxWidth();
    ScrollTrigger.addEventListener('refreshInit', getMaxWidth);
    layout.onEnd(() => ScrollTrigger.removeEventListener('refreshInit', getMaxWidth));

    // How far the strip moves: until its last article's right edge meets the right edge of
    // the visible page. The window width is the wrong measure: the strip starts after the
    // header (`.left-header-space`) and the page ends at the scrollbar, and using it left the
    // last ~100px of the end screen out of reach. (Not the wrapper's own width either: the pin
    // fixes that a scrollbar narrower.)
    const inset = scrollContainer.closest('.left-header-space');
    const travel = () => Math.max(maxWidth - (document.documentElement.clientWidth - (inset ? parseFloat(getComputedStyle(inset).paddingLeft) : 0)), 0);

    const scrollTween = gsap.to(scrollContainer, {
        x: () => -travel(),
        ease: 'none'
    });
    mag.scrollTween = scrollTween;

    const horizontalScroll = ScrollTrigger.create({
        animation: scrollTween,
        trigger: '.wrapper',
        pin: true,
        speed: 2,
        scrub: 1,
        end: () => `+=${maxWidth}`,
        invalidateOnRefresh: true
    });

    const dragRatio = maxWidth / (travel() || 1);
    layout.onEnd(() => {
        Draggable.get('.drag-proxy')?.kill();
        mag.scrollTween = null;
        mag.goToArticle = null;
        // the strip's tween leaves translate(0, 0) behind; any inline transform would make
        // the wrapper the containing block of everything position: fixed inside it
        scrollContainer.style.transform = '';
    });
    Draggable.create('.drag-proxy', {
        trigger: '.wrapper',
        type: 'x',
        allowContextMenu: true,
        onPress() {
            this.startScroll = horizontalScroll.scroll();
        },
        onDrag() {
            horizontalScroll.scroll(this.startScroll - (this.x - this.startX) * dragRatio);
        }
    });

    // Scrolls an article flush against the site header.
    //
    // Where that is gets read off the horizontal tween instead of computed from
    // `maxWidth`/`offsetLeft`: sample where the article sits at either end of the tween,
    // then interpolate to the header edge. Working it out by hand needs two numbers that
    // quietly drift — the pinned wrapper is already inset by the header width
    // (`.left-header-space`), and the tween's travel is only re-evaluated on refresh, so it
    // does not have to match the current `maxWidth - innerWidth`. Sampling is immune to both.
    mag.goToArticle = (id, animate) => {
        const targetElement = document.getElementById(id);
        // the target article can be filtered out by personalisation
        if (!targetElement) {
            return;
        }

        // suppressEvents, so sampling doesn't fire the parallax triggers that hang off
        // this same tween
        const resume = scrollTween.progress();
        const leftAt = (progress) => {
            scrollTween.progress(progress, true);
            return targetElement.getBoundingClientRect().left;
        };
        const atStart = leftAt(0);
        const travel = atStart - leftAt(1);
        scrollTween.progress(resume, true);

        // everything fits on screen, so there is nowhere to scroll to
        if (travel <= 0) {
            return;
        }

        const navBar = document.querySelector('.site-header');
        const progress = Math.min(Math.max((atStart - navBar.offsetWidth) / travel, 0), 1);
        const to = horizontalScroll.start + progress * (horizontalScroll.end - horizontalScroll.start);
        gsap.to(window, {
            scrollTo: to,
            duration: animate ? mag.jumpDuration(to - window.scrollY, 0.8, 2.2, 1 / 4000) : 0,
            ease: 'power3.inOut',
            overwrite: 'auto'
        });
    };

    // The pin is built around the page as it was when this ran, and the browser's own
    // fragment jump happened even earlier — before the pin existed at all. Reset to a known
    // state so the jump below measures against the real layout.
    if (deepLinkId) {
        window.scrollTo(0, 0);
        ScrollTrigger.refresh();
    }
};

mag.initMobileScroller = (layout) => {
    const header = document.querySelector('.site-header');
    const progress = document.getElementById('myProgress');
    layout.onEnd(() => {
        if (progress) {
            progress.style.top = '';
        }
        mag.goToArticle = null;
    });
    if (header && progress) {
        // Kept in step with the header's height: it changes once the brand sheet, which holds the
        // header styles, arrives — possibly after this runs.
        const place = () => {
            // padding box, unrounded (clientHeight rounds to whole pixels)
            const cs = getComputedStyle(header);
            const top = header.getBoundingClientRect().height - parseFloat(cs.borderTopWidth) - parseFloat(cs.borderBottomWidth);
            progress.style.top = `${top}px`;
        };
        place();
        layout.observe(new ResizeObserver(place)).observe(header);
    }

    // In-page anchors (from article HTML written in the CMS) scroll smoothly instead of
    // jumping. On capture: `<base href="/">` turns "#x" into "/#x", which Blazor's own
    // document-level link handler would otherwise navigate to. Article links are left to
    // initArticleNavigation(), which also keeps the address bar in step.
    document.addEventListener('click', (e) => {
        const link = e.target instanceof Element ? e.target.closest('a[href^="#"]') : null;
        if (!link || /^#article-\d+$/.test(link.getAttribute('href'))) {
            return;
        }
        e.preventDefault();
        let target = null;
        try {
            target = document.querySelector(link.getAttribute('href'));
        } catch {
            // a bare "#" or anything else that isn't a selector: nowhere to go
        }
        if (target) {
            mag.scrollToY(mag.pageTop(target) - 50, 500);
        }
    }, { capture: true, signal: layout.signal });

    // mobile stacks the sections vertically, so a plain vertical scroll is all a jump needs
    mag.goToArticle = (id, animate) => {
        const targetElement = document.getElementById(id);
        // the target article can be filtered out by personalisation
        if (!targetElement) {
            return;
        }
        const to = mag.pageTop(targetElement) - 50;
        mag.scrollToY(to, animate ? mag.jumpDuration(to - window.scrollY, 500, 1400, 1 / 6) : 0);
    };
};

// Covers the header menu, the contents blocks and internal (anchor type) article buttons —
// anything pointing at `#article-{id}`.
mag.initArticleNavigation = (session, deepLinkId) => {
    // Delegated from the document rather than bound per anchor, because Blazor re-renders
    // these blocks over the life of a circuit and anything bound to the individual anchors
    // dies with the nodes it was bound to. On capture, so it does not depend on running
    // before Blazor's own document-level click handler, which would otherwise turn the
    // click into a page navigation.
    document.addEventListener('click', (e) => {
        const link = e.target instanceof Element ? e.target.closest('a[href*="#article-"]') : null;
        if (!link || !mag.goToArticle) {
            return;
        }

        const id = link.getAttribute('href').split('#')[1];
        if (!/^article-\d+$/.test(id)) {
            return;
        }

        e.preventDefault();
        // Keep the address bar on the article being read, so it can be shared or reloaded.
        // This has to be us rather than the browser: letting the click navigate does nothing
        // at all when the href already matches the current URL — which is exactly the state
        // a deep link (or an earlier jump) leaves the page in.
        //
        // Spelled out from `location` rather than passing a bare '#id': relative URLs here
        // resolve against `<base href="/">`, which the Blazor host sets, so the magazine
        // would drop out of the path.
        history.replaceState(history.state, '', window.location.pathname + window.location.search + '#' + id);
        mag.goToArticle(id, true);
    }, { capture: true, signal: session.signal });

    // Deep link — /browse/{name}#article-{id}. The browser's own fragment jump fires before
    // Blazor has rendered the articles, so it lands nowhere useful (and on desktop, before
    // the horizontal strip is even pinned). Redo it, without the animation.
    if (deepLinkId && mag.goToArticle) {
        mag.goToArticle(deepLinkId, false);
    }

    // same, for a hash typed into the address bar or reached with back/forward
    window.addEventListener('hashchange', () => {
        if (mag.goToArticle && /^#article-\d+$/.test(window.location.hash)) {
            mag.goToArticle(window.location.hash.slice(1), true);
        }
    }, { signal: session.signal });
};
